import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatCard from '../components/dashboard/StatCard';
import '../styles/AnalyticsPage.css';
import { getDashboardStats, getTransactionStats, getWorkerPerformance, getAreaAnalytics, getAnalyticsSeries } from '../utils/api';
import {TrendingUp, Users, Briefcase, DollarSign, Star, Activity, CheckCircle, PieChartIcon, MapPin, BarChart3, Sparkles
} from 'lucide-react';

const AnalyticsPage = () => {
  const [period, setPeriod] = useState('month');

  const { data: stats, isLoading: statsLoading } = useApi(getDashboardStats, { immediate: true });
  const { data: transStats } = useApi(getTransactionStats, { immediate: true });
  const { data: workerPerf } = useApi(getWorkerPerformance, { immediate: true });
  const { data: areaData } = useApi(getAreaAnalytics, { immediate: true });
  const { data: series }   = useApi(getAnalyticsSeries, { immediate: true });

  const periods = [
    { value: 'week', label: 'Esta Semana' },
    { value: 'month', label: 'Este Mês' },
    { value: 'quarter', label: 'Este Trimestre' },
    { value: 'year', label: 'Este Ano' },
  ];

  // Dados reais dos últimos 12 meses (/admin/analytics/series). Meses sem
  // actividade aparecem a zero — antes havia aqui curvas inventadas.
  const PALETTE = ['#14b8a6', '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];
  const chartData = {
    revenue:  series?.revenue  || [],
    users:    series?.users    || [],
    requests: series?.requests || [],
    categories: (series?.categories || []).map((c, i) => ({ ...c, color: PALETTE[i % PALETTE.length] })),
  };
  const taxaConclusao = Number(stats?.total_requests) > 0
    ? Math.round((Number(stats.completed_requests) / Number(stats.total_requests)) * 100)
    : null;

  const BarChart = ({ data, color = '#14b8a6', height = 200 }) => {
    // guarda contra divisão por zero quando ainda não há actividade
    const max = Math.max(...data, 1);
    if (!data.length) {
      return <div className="h-[200px] flex items-center justify-center text-gray-500 text-sm">Sem dados para o período</div>;
    }
    return (
      <div className="flex items-end gap-2 h-[200px] pt-4">
        {data.map((val, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
            <div className="relative w-full">
              <div
                className="w-full rounded-t-md transition-all duration-500 group-hover:opacity-80"
                style={{
                  height: `${(val / max) * height}px`,
                  backgroundColor: color,
                  opacity: 0.7 + (i / data.length) * 0.3,
                }}
              />
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 
                              transition-opacity bg-gray-700 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                {val}
              </div>
            </div>
            <span className="text-[10px] text-gray-500">{i + 1}</span>
          </div>
        ))}
      </div>
    );
  };

  const PieChartComponent = ({ data }) => {
    const total = data.reduce((acc, d) => acc + d.value, 0);
    let currentAngle = 0;

    if (!data.length || total === 0) {
      return <div className="h-40 flex items-center justify-center text-gray-500 text-sm">Ainda não há pedidos por categoria</div>;
    }

    return (
      <div className="flex items-center gap-8">
        <svg viewBox="0 0 100 100" className="w-40 h-40 -rotate-90">
          {data.map((segment, i) => {
            const angle = (segment.value / total) * 360;
            const x1 = 50 + 40 * Math.cos((currentAngle * Math.PI) / 180);
            const y1 = 50 + 40 * Math.sin((currentAngle * Math.PI) / 180);
            const x2 = 50 + 40 * Math.cos(((currentAngle + angle) * Math.PI) / 180);
            const y2 = 50 + 40 * Math.sin(((currentAngle + angle) * Math.PI) / 180);
            const largeArc = angle > 180 ? 1 : 0;

            const path = `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`;
            // const startAngle = currentAngle;
            currentAngle += angle;

            return (
              <path
                key={i}
                d={path}
                fill={segment.color}
                stroke="rgba(31, 41, 55, 1)"
                strokeWidth="2"
                className="hover:opacity-80 transition-opacity cursor-pointer"
              >
                <title>{segment.name}: {segment.value}%</title>
              </path>
            );
          })}
        </svg>
        <div className="space-y-2">
          {data.map((segment, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: segment.color }} />
              <span className="text-sm text-gray-300">{segment.name}</span>
              <span className="text-sm text-gray-500">{segment.value} ({Math.round((segment.value / total) * 100)}%)</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (statsLoading && !stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="analytics-admin-page space-y-6">
      <section className="analytics-admin-hero">
        <div>
          <span className="analytics-admin-eyebrow"><Sparkles className="w-4 h-4" /> Inteligência operacional</span>
          <h1>Análises e desempenho</h1>
          <p>Transforme a atividade da plataforma em decisões claras para crescimento, qualidade e cobertura.</p>
        </div>
        <div className="analytics-periods">
          <span><BarChart3 className="w-4 h-4" /> Período</span>
          <div>
          {periods.map(p => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={period === p.value ? 'is-active' : ''}
            >
              {p.label}
            </button>
          ))}
          </div>
        </div>
      </section>

      {/* KPI Cards */}
      <div className="analytics-kpis grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Volume Libertado"
          value={transStats?.total_revenue === undefined ? '—' : `Kz ${Number(transStats.total_revenue).toLocaleString('pt-PT')}`}
          subtitle="Valor protegido pago aos profissionais"
          icon={DollarSign}
          color="green"
        />
        <StatCard
          title="Utilizadores"
          value={stats?.total_users === undefined ? '—' : Number(stats.total_users).toLocaleString('pt-PT')}
          subtitle="Clientes e profissionais"
          icon={Users}
          color="blue"
        />
        <StatCard
          title="Taxa de Conclusão"
          value={taxaConclusao === null ? '—' : `${taxaConclusao}%`}
          subtitle={stats?.total_requests ? `${stats.completed_requests} de ${stats.total_requests} pedidos` : 'Ainda sem pedidos'}
          icon={CheckCircle}
          color="teal"
        />
        <StatCard
          title="Satisfação"
          value={Number(stats?.rated_reviews) ? Number(stats.avg_rating).toFixed(1) : '—'}
          subtitle={Number(stats?.rated_reviews) ? `De 5 estrelas · ${stats.rated_reviews} avaliações` : 'Ainda sem avaliações válidas'}
          icon={Star}
          color="cyan"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className="analytics-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-teal-400" />
              Receita ao Longo do Tempo
            </h3>
            <span className="text-sm text-gray-400">Últimos 12 meses</span>
          </div>
          <BarChart data={chartData.revenue} color="#14b8a6" />
        </div>

        {/* Users Chart */}
        <div className="analytics-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" />
              Novos Utilizadores
            </h3>
            <span className="text-sm text-gray-400">Últimos 12 meses</span>
          </div>
          <BarChart data={chartData.users} color="#3b82f6" />
        </div>
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Distribution */}
        <div className="analytics-panel">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
            <PieChartIcon className="w-5 h-5 text-purple-400" />
            Distribuição por Categoria
          </h3>
          <PieChartComponent data={chartData.categories} />
        </div>

        {/* Requests Chart */}
        <div className="analytics-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-orange-400" />
              Pedidos por mês
            </h3>
            <span className="text-sm text-gray-400">Últimos 12 meses</span>
          </div>
          <BarChart data={chartData.requests} color="#f59e0b" />
        </div>
      </div>

      {/* Por área de Luanda — trabalhadores vs. solicitações */}
      <div className="analytics-panel analytics-panel--wide">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-1">
          <MapPin className="w-5 h-5 text-teal-400" />
          Por Área de Luanda
        </h3>
        <p className="text-gray-400 text-sm mb-4">
          Profissionais com localização ativa e pedidos dos últimos {areaData?.period_days || 90} dias, por município/zona.
          Zonas com muitos pedidos e poucos profissionais são oportunidades de recrutamento.
        </p>
        {(areaData?.areas || []).length === 0 ? (
          <p className="text-gray-500 text-sm py-6 text-center">
            Ainda sem dados de área — aparecem quando houver profissionais a partilhar localização e pedidos com morada.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase">Área</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase">Profissionais</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase">Ativos (&lt;30 min)</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase">Pedidos (90d)</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase">Abertas</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase">Cobertura</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {(areaData?.areas || []).map((a) => {
                  const maxReq = Math.max(...(areaData?.areas || []).map(x => x.requests), 1);
                  const gap = a.requests > 0 && a.workers === 0;
                  return (
                    <tr key={a.area} className="hover:bg-gray-700/30 transition-colors">
                      <td className="py-3 px-4 text-white font-medium">{a.area}</td>
                      <td className="py-3 px-4 text-gray-300">{a.workers}</td>
                      <td className="py-3 px-4 text-green-400">{a.workers_recent}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-gray-700 rounded-full overflow-hidden">
                            <div className="h-full bg-amber-500 rounded-full" style={{ width: `${(a.requests / maxReq) * 100}%` }} />
                          </div>
                          <span className="text-gray-300 text-sm">{a.requests}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-blue-400">{a.open_requests}</td>
                      <td className="py-3 px-4">
                        {gap ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                            Sem cobertura
                          </span>
                        ) : a.requests > a.workers * 3 ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400">
                            Precisa de mais profissionais
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                            Equilibrada
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Top Workers Table */}
      <div className="analytics-panel analytics-panel--wide">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <Briefcase className="w-5 h-5 text-teal-400" />
          Profissionais em destaque
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase">Rank</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase">Nome</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase">Serviços</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase">Avaliação</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase">Ganhos</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase">Taxa Aceitação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {(workerPerf || []).slice(0, 10).map((worker, idx) => (
                <tr key={worker.id} className="hover:bg-gray-700/30 transition-colors">
                  <td className="py-3 px-4">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold
                      ${idx === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                        idx === 1 ? 'bg-gray-400/20 text-gray-300' :
                        idx === 2 ? 'bg-orange-600/20 text-orange-400' :
                        'bg-gray-700 text-gray-400'}`}>
                      {idx + 1}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-white font-medium">{worker.full_name}</td>
                  <td className="py-3 px-4 text-gray-300">{worker.completed_jobs || 0}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <span className="text-gray-300">{worker.rating || 0}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-green-400">
                    Kz {Number(worker.earnings || 0).toLocaleString('pt-PT')}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-teal-500 rounded-full"
                          style={{ width: `${worker.acceptance_rate || 0}%` }}
                        />
                      </div>
                      <span className="text-gray-400 text-sm">{worker.acceptance_rate || 0}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
